import React, { useState, useEffect } from 'react';
import './AddColorPage.css';
import editIcon from "../assets/icons/EditIcon.svg";
import deleteIcon from "../assets/icons/DeleteIcon.svg";

const AddColorPage = () => {
    // 1. State Management
    const [colors, setColors] = useState([]);
    const [permissions, setPermissions] = useState([]); 
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingColor, setEditingColor] = useState(null);
    const [formData, setFormData] = useState({ name: '', number: '' });

    // 2. Computed Logic
    const canDelete = permissions.includes("model_delete_button");

    // 3. Fetch Data
    const fetchColors = async () => {
        try {
            const response = await fetch('/api/colors');
            const data = await response.json();
            setColors(data);
        } catch (err) {
            console.error("Fetch error:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchColors();
        const storedPermissions = JSON.parse(localStorage.getItem("permissions") || "[]");
        setPermissions(storedPermissions);
    }, []);

    // 4. Handlers
    const handleOpenModal = (color = null) => {
        if (color) {
            setEditingColor(color);
            setFormData({ 
                name: color.name || '', 
                number: color.number || '' 
            });
        } else {
            setEditingColor(null);
            setFormData({ name: '', number: '' });
        }
        setIsModalOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const isEditing = !!editingColor;
        const id = editingColor?.id; 
        const url = isEditing ? `/api/colors/${id}` : '/api/colors';
        const payload = { name: formData.name, number: formData.number };

        try {
            const response = await fetch(url, {
                method: isEditing ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                setIsModalOpen(false);
                setEditingColor(null);
                setFormData({ name: '', number: '' });
                fetchColors();
            } else {
                const errorData = await response.json();
                alert(`Error: ${errorData.error || "Failed to save"}`);
            }
        } catch (err) {
            console.error("Submission error:", err);
        }
    };

    const handleDelete = async (id) => {
        if (!canDelete) return; 
        if (!window.confirm("Are you sure you want to delete this color?")) return;
        
        try {
            const response = await fetch(`/api/colors/${id}`, { method: 'DELETE' });
            if (response.ok) {
                fetchColors();
            } else {
                const errorData = await response.json();
                alert(`Error: ${errorData.error}`);
            }
        } catch (err) {
            console.error("Delete error:", err);
        }
    };

    if (loading) {
        return <div className="loader">Loading...</div>;
    }

    return (
        <div className="add-color-container">
            <div className="management-header">
                <h2>Color Management</h2>
            </div>

            <button className="new-color-button" onClick={() => handleOpenModal()}>
                + New Color
            </button>
            
            <div className="UserList-container">
                <table>
                    <thead>
                        <tr>
                            <th>Color Name</th>
                            <th>Color Number</th>
                            <th style={{ textAlign: 'center' }}>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {colors.map((color, index) => (
                            <tr key={`${color.id}-${index}`}>
                                <td>{color.name}</td>
                                <td>{color.number || "-"}</td>
                                <td style={{ textAlign: 'center' }}>
                                    <div className="action-buttons-cell">
                                        {/* Edit Icon Button */}
                                        <button 
                                            className="icon-action-btn edit-btn" 
                                            onClick={() => handleOpenModal(color)}
                                            title="Edit Color"
                                        >
                                            <img src={editIcon} alt="Edit" />
                                        </button>

                                        {/* Delete Icon Button */}
                                        <button 
                                            className="icon-action-btn delete-btn" 
                                            onClick={() => handleDelete(color.id)}
                                            disabled={!canDelete}
                                            title={canDelete ? "Delete Color" : "No Permission"}
                                            style={{ 
                                                opacity: canDelete ? 1 : 0.3, 
                                                cursor: canDelete ? 'pointer' : 'not-allowed'
                                            }}
                                        >
                                            <img 
                                                src={deleteIcon} 
                                                alt="Delete" 
                                                style={{ filter: canDelete ? 'none' : 'grayscale(100%)' }} 
                                            />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h3>{editingColor ? 'Edit Color' : 'Add New Color'}</h3>
                        <form onSubmit={handleSubmit}>
                            <input 
                                type="text" 
                                value={formData.name}
                                onChange={(e) => setFormData({...formData, name: e.target.value})}
                                placeholder="Color Name"
                                required 
                            />
                            <input 
                                type="text" 
                                value={formData.number}
                                onChange={(e) => setFormData({...formData, number: e.target.value})}
                                placeholder="Color Number"
                            />
                            <div className="modal-actions">
                                <button type="submit">Save</button>
                                <button type="button" onClick={() => setIsModalOpen(false)}>
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AddColorPage;